import CryptoJS from 'crypto-js'
import SparkMD5 from 'spark-md5'

/**
 * 浏览器兼容的文件工具类
 * 替代 Node.js 的 Buffer 和 fs 操作
 */
export class BrowserFileUtils {
  private static textDecoders = new Map<string, TextDecoder>()

  public static BOOL_LENGTH: number = 1
  public static INT_LENGTH: number = 4
  public static INT16_LENGTH: number = 2
  public static MSSTRING_LENGTH: number = 6
  public static DOUBLE_LENGTH: number = 8

  /**
   * 字节数组转化为 int8
   */
  public static bytesToInt8(bytes: Uint8Array): number {
    if (bytes.byteLength < 1) return 0
    return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint8(0)
  }

  /**
   * 字节数组转化为 int32 (Little Endian)
   */
  public static bytesToInt32(bytes: Uint8Array): number {
    if (bytes.byteLength < 4) return 0
    return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getInt32(0, true)
  }

  /**
   * 字节数组转化为 uint32 (Little Endian)
   */
  public static bytesToUInt32(bytes: Uint8Array): number {
    if (bytes.byteLength < 4) return 0
    return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(0, true)
  }

  /**
   * 字节数组转化为 int32 (Big Endian)
   */
  public static bytesToInt32Be(bytes: Uint8Array): number {
    if (bytes.byteLength < 4) return 0
    return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getInt32(0, false)
  }

  /**
   * 字节数组转化为 int16 (Big Endian)
   */
  public static bytesToInt16(bytes: Uint8Array): number {
    if (bytes.byteLength < 2) return 0
    return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getInt16(0, false)
  }

  /**
   * 字节数组转化为 uint8
   */
  public static bytesToUInt8(bytes: Uint8Array): number {
    return bytes[0]
  }

  /**
   * 字节转化为字符串
   */
  public static bytesToString(bytes: Uint8Array | undefined | null, encoding: string = 'utf-8'): string {
    if (!bytes || bytes.byteLength <= 0) return ''
    if (typeof TextDecoder !== 'undefined') {
      try {
        let decoder = this.textDecoders.get(encoding)
        if (!decoder) {
          decoder = new TextDecoder(encoding)
          this.textDecoders.set(encoding, decoder)
        }
        return decoder.decode(bytes)
      } catch {
        // 旧版 iPad Safari 的 TextDecoder 支持不稳定，失败时走手写 UTF-8 解码。
      }
    }
    return this.decodeUtf8Bytes(bytes)
  }

  private static decodeUtf8Bytes(bytes: Uint8Array): string {
    let output = ''
    for (let i = 0; i < bytes.length; i++) {
      const first = bytes[i] ?? 0
      if (first < 0x80) {
        output += String.fromCharCode(first)
        continue
      }
      if (first >= 0xc0 && first < 0xe0 && i + 1 < bytes.length) {
        const second = bytes[++i] ?? 0
        output += String.fromCharCode(((first & 0x1f) << 6) | (second & 0x3f))
        continue
      }
      if (first >= 0xe0 && first < 0xf0 && i + 2 < bytes.length) {
        const second = bytes[++i] ?? 0
        const third = bytes[++i] ?? 0
        output += String.fromCharCode(((first & 0x0f) << 12) | ((second & 0x3f) << 6) | (third & 0x3f))
        continue
      }
      if (first >= 0xf0 && first < 0xf8 && i + 3 < bytes.length) {
        const second = bytes[++i] ?? 0
        const third = bytes[++i] ?? 0
        const fourth = bytes[++i] ?? 0
        const codePoint = ((first & 0x07) << 18) | ((second & 0x3f) << 12) | ((third & 0x3f) << 6) | (fourth & 0x3f)
        const offset = codePoint - 0x10000
        output += String.fromCharCode(0xd800 + (offset >> 10), 0xdc00 + (offset & 0x3ff))
        continue
      }
      output += String.fromCharCode(first)
    }
    return output
  }

  /**
   * 字节数组转化为 int64 (返回 number，精度有限)
   */
  public static bytesToInt64(bytes: Uint8Array): number {
    if (bytes.byteLength < 8) return 0
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    const low = view.getUint32(0, true)
    const high = view.getInt32(4, true)
    return high * 0x100000000 + low
  }

  /**
   * 字节数组转化为 uint64 (返回 bigint)
   */
  public static bytesToUInt64(bytes: Uint8Array): bigint {
    if (bytes.byteLength < 8) return BigInt(0)
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    return view.getBigUint64(0, true)
  }

  /**
   * 字节数组转化为 int64 (返回 bigint)
   */
  public static bytesToInt64_2(bytes: Uint8Array): bigint {
    if (bytes.byteLength < 8) return BigInt(0)
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    return view.getBigInt64(0, true)
  }

  /**
   * int32 转化为字节数组
   */
  public static int32ToBytes(num: number): Uint8Array {
    const result = new Uint8Array(4)
    result[0] = num & 0xFF
    result[1] = (num >> 8) & 0xFF
    result[2] = (num >> 16) & 0xFF
    result[3] = (num >> 24) & 0xFF
    return result
  }

  /**
   * int32 转化为数字数组
   */
  public static int32ToArrayNumber(num: number): number[] {
    return [
      num & 0xFF,
      (num >> 8) & 0xFF,
      (num >> 16) & 0xFF,
      (num >> 24) & 0xFF
    ]
  }

  /**
   * int64 转化为数字数组
   */
  public static int64ToArrayNumber(num: number): number[] {
    const _num1 = Math.floor(num / 0x100000000)
    const _num2 = num % 0x100000000
    return [
      _num2 & 0xFF,
      (_num2 >> 8) & 0xFF,
      (_num2 >> 16) & 0xFF,
      (_num2 >> 24) & 0xFF,
      _num1 & 0xFF,
      (_num1 >> 8) & 0xFF,
      (_num1 >> 16) & 0xFF,
      (_num1 >> 24) & 0xFF
    ]
  }

  /**
   * 字符串转化为字节数组 (UTF-8)
   */
  public static stringToByte(str: string): Uint8Array {
    const encoder = new TextEncoder()
    return encoder.encode(str)
  }

  /**
   * 字符串转化为数字数组
   */
  public static stringToArrayNumber(str: string): number[] {
    const bytes = this.stringToByte(str)
    return Array.from(bytes)
  }

  /**
   * 字符串转化为 unicode 数组
   */
  public static string2unicode(str: string): number[] {
    const bytes: number[] = []
    for (let i = 0; i < str.length; i++) {
      const c = str.charCodeAt(i)
      bytes.push(c & 0xFF)
      bytes.push((c >> 8) & 0xFF)
    }
    return bytes
  }

  /**
   * 字节数组转化为字符串
   */
  public static byteToString(arr: number[]): string {
    const bytes = new Uint8Array(arr)
    return new TextDecoder('utf-8').decode(bytes)
  }

  /**
   * 字节转化为浮点数
   */
  public static bytes2Float(bytes: Uint8Array, isDouble: boolean = false): number {
    if (isDouble) {
      if (bytes.byteLength < 8) return 0
      return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getFloat64(0, true)
    } else {
      if (bytes.byteLength < 4) return 0
      return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getFloat32(0, true)
    }
  }

  /**
   * 浮点数转化为数字数组
   */
  public static float2ArrayNumber(num: number, isDouble: boolean = false): number[] {
    const len = isDouble ? 8 : 4
    const buffer = new ArrayBuffer(len)
    const view = new DataView(buffer)
    if (isDouble) {
      view.setFloat64(0, num, true)
    } else {
      view.setFloat32(0, num, true)
    }
    return Array.from(new Uint8Array(buffer))
  }

  /**
   * 浮点数转化为字节数组
   */
  public static float2Bytes(num: number, isDouble: boolean = false): Uint8Array {
    const len = isDouble ? 8 : 4
    const buffer = new ArrayBuffer(len)
    const view = new DataView(buffer)
    if (isDouble) {
      view.setFloat64(0, num, true)
    } else {
      view.setFloat32(0, num, true)
    }
    return new Uint8Array(buffer)
  }

  /**
   * boolean 转化为字节数组
   */
  public static bool2Bytes(b: boolean): Uint8Array {
    return new Uint8Array([b ? 0x01 : 0x00])
  }

  /**
   * boolean 转化为数字数组
   */
  public static bool2ArrayNumber(b: boolean): number[] {
    return [b ? 0x01 : 0x00]
  }

  /**
   * 字节转化为 boolean
   */
  public static bytes2Bool(bytes: Uint8Array): boolean {
    return bytes[0] !== 0x00
  }

  /**
   * 从字符串计算 MD5
   */
  public static getMd5HashFromString(s: string): string {
    return CryptoJS.MD5(s).toString()
  }

  /**
   * 字符串转化为十六进制
   */
  public static stringtoHex(str: string): string {
    if (str === "") return ""
    const bytes = this.stringToByte(str)
    const hexCharCode: string[] = []
    for (let i = 0; i < bytes.length; i++) {
      hexCharCode.push(bytes[i].toString(16).padStart(2, '0'))
    }
    return hexCharCode.join("").toUpperCase()
  }

  /** 从 ArrayBuffer 计算 MD5，和文件上传协议保持同一套 SparkMD5 算法。 */
  public static getMd5HashFromArrayBuffer(buffer: ArrayBuffer): string {
    const spark = new SparkMD5.ArrayBuffer()
    spark.append(buffer)
    return spark.end()
  }

  /** 从 Uint8Array 计算 MD5，自动裁剪有效视图范围。 */
  public static getMd5HashFromUint8Array(bytes: Uint8Array): string {
    const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
    return this.getMd5HashFromArrayBuffer(buffer)
  }

  /**
   * 读取 Blob/File 为 ArrayBuffer，兼容旧版 iPad 浏览器缺失 Blob.arrayBuffer 的情况。
   * @param blob 需要读取的 Blob 或 File。
   * @returns 文件二进制内容。
   */
  public static async blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    if (typeof blob.arrayBuffer === 'function') {
      return await blob.arrayBuffer()
    }
    return await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result)
          return
        }
        reject(new Error('Blob 读取失败：未返回 ArrayBuffer'))
      }
      reader.onerror = () => reject(reader.error ?? new Error('Blob 读取失败'))
      reader.readAsArrayBuffer(blob)
    })
  }

  /**
   * 读取 Blob/File 为文本，兼容旧版浏览器缺失 Blob.text 的情况。
   * @param blob 需要读取的 Blob 或 File。
   * @returns 文本内容。
   */
  public static async blobToText(blob: Blob): Promise<string> {
    if (typeof blob.text === 'function') {
      return await blob.text()
    }
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result ?? ''))
      reader.onerror = () => reject(reader.error ?? new Error('Blob 文本读取失败'))
      reader.readAsText(blob)
    })
  }

  /**
   * 读取 Fetch 响应为 Uint8Array，优先走 response.arrayBuffer，缺失时回退 Blob/FileReader。
   * @param response Fetch 返回对象。
   * @returns 响应二进制字节。
   */
  public static async responseToUint8Array(response: Response): Promise<Uint8Array> {
    if (typeof response.arrayBuffer === 'function') {
      return new Uint8Array(await response.arrayBuffer())
    }
    return new Uint8Array(await this.blobToArrayBuffer(await response.blob()))
  }

  /**
   * 异步计算大文件 MD5 (分块计算，不会阻塞主线程)
   */
  public static async getMd5HashFromFile(file: File, onProgress?: (percent: number) => void): Promise<string> {
    return new Promise((resolve, reject) => {
      const chunkSize = 2 * 1024 * 1024 // 2MB chunks
      const spark = new SparkMD5.ArrayBuffer()
      const reader = new FileReader()
      const chunks = Math.ceil(file.size / chunkSize)
      let currentChunk = 0

      reader.onload = (e) => {
        if (e.target?.result) {
          spark.append(e.target.result as ArrayBuffer)
        }
        currentChunk++

        if (onProgress) {
          onProgress(Math.round((currentChunk / chunks) * 100))
        }

        if (currentChunk < chunks) {
          loadNext()
        } else {
          resolve(spark.end())
        }
      }

      reader.onerror = () => {
        reject(new Error('File read error'))
      }

      const loadNext = () => {
        const start = currentChunk * chunkSize
        const end = Math.min(start + chunkSize, file.size)
        reader.readAsArrayBuffer(file.slice(start, end))
      }

      loadNext()
    })
  }

  /**
   * 获取文件大小
   */
  public static fileSize(file: File): number {
    return file.size
  }

  /**
   * Uint8Array 转数组
   */
  public static bytes2Array(bytes: Uint8Array): number[] {
    return Array.from(bytes)
  }

  /**
   * 合并多个 Uint8Array
   */
  public static concatUint8Arrays(...arrays: Uint8Array[]): Uint8Array {
    const totalLength = arrays.reduce((acc, arr) => acc + arr.length, 0)
    const result = new Uint8Array(totalLength)
    let offset = 0
    for (const arr of arrays) {
      result.set(arr, offset)
      offset += arr.length
    }
    return result
  }
}

export default BrowserFileUtils
